import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, OnChanges, Output } from '@angular/core';

import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { Store } from '@ngrx/store';

import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipType } from '../../../../core/betslip-type';
import { BetslipNotificationActions } from '../../../notifications/betslip-notification.actions';
import { RewardTokenContext } from '../../../reward-tokens/reward-tokens.model';
import { ErrorViewModel } from '../../models/models';
import { ClientErrorTrackingService } from '../../services/tracking/client-error-tracking.service';

export interface SummaryErrorsViewModel {
    errors: ErrorViewModel[];
    betBuilderNotification?: string | null;
    isOfferChanged?: boolean;
    isBetPlacementOfferChanged?: boolean;
    hasComboPrevention?: boolean;
    betslipType?: BetslipType | null;
}

@Component({
    selector: 'bs-errors-container',
    templateUrl: './errors-container.component.html',
    styleUrls: ['./errors-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ErrorsContainerComponent implements OnChanges {
    @Output() errorDisplayChange = new EventEmitter<boolean>();
    @Input() viewModel: SummaryErrorsViewModel;
    @Input() tokensContext?: RewardTokenContext;

    @HostListener('document:click')
    onClick() {
        if (!this.showBetBuilderNotification) {
            return;
        }

        this.store.dispatch(BetslipNotificationActions.clearMessage());
    }

    constructor(
        private store: Store<IBetslipRootState>,
        private trackingService: ClientErrorTrackingService,
    ) {}

    ngOnChanges(changes: ISimpleChanges<ErrorsContainerComponent>): void {
        const viewModel = changes.viewModel?.currentValue;

        if (!viewModel) {
            this.notifyErrorDisplayedChanged(false, false, false, false);

            return;
        }

        const errors = viewModel.errors;

        this.notifyErrorDisplayedChanged(
            errors.length > 0,
            !!viewModel.betBuilderNotification,
            (viewModel.isOfferChanged || viewModel.isBetPlacementOfferChanged) ?? false,
            viewModel.hasComboPrevention ?? false,
        );

        const previous = changes.viewModel?.previousValue;

        // We check the viewmodel here to avoid tracking the same error multiple times
        if (!this.hasViewModelChanged(viewModel, previous)) {
            return;
        }

        this.trackingService.trackAllErrors(
            errors,
            viewModel.hasComboPrevention ?? false,
            viewModel.betslipType ?? null,
            viewModel.isOfferChanged ?? false,
        );
    }

    get showBetBuilderNotification(): boolean {
        return !!this.viewModel?.betBuilderNotification;
    }

    get isOfferChanged(): boolean {
        return (this.viewModel?.isOfferChanged || this.viewModel?.isBetPlacementOfferChanged) ?? false;
    }

    get showBetslipErrors(): boolean {
        return !this.viewModel?.hasComboPrevention && this.viewModel?.errors.length > 0;
    }

    private notifyErrorDisplayedChanged(
        hasBetslipErrors: boolean,
        hasBetBuilderNotification: boolean,
        isOfferChanged: boolean,
        hasComboPrevention: boolean,
    ): void {
        const isErrorDisplayed = hasBetslipErrors || hasBetBuilderNotification || isOfferChanged || hasComboPrevention;

        this.errorDisplayChange.emit(isErrorDisplayed);
    }

    private hasViewModelChanged(current: SummaryErrorsViewModel, previous: SummaryErrorsViewModel | undefined): boolean {
        if (!previous) {
            return true;
        }

        const hasComboPreventionChanged = current.hasComboPrevention !== previous.hasComboPrevention;
        const hasOfferStatusChanged = current.isOfferChanged !== previous.isOfferChanged;
        const hasBetslipTypeChanged = current.betslipType !== previous.betslipType;

        if (hasComboPreventionChanged || hasOfferStatusChanged || hasBetslipTypeChanged) {
            return true;
        }

        const currentTrackedError = this.trackingService.getFirstTrackableError(current.errors);
        const previousTrackedError = this.trackingService.getFirstTrackableError(previous.errors);

        if (!currentTrackedError) {
            return false;
        }

        if (!previousTrackedError) {
            return true;
        }

        return currentTrackedError.error.type !== previousTrackedError.error.type || currentTrackedError.message !== previousTrackedError.message;
    }
}
